import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { TextInput } from '@cbhq/cds-web/controls/TextInput';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { ModalBody } from '@cbhq/cds-web/overlays/modal/ModalBody';
import { ModalFooter } from '@cbhq/cds-web/overlays/modal/ModalFooter';
import { ModalHeader } from '@cbhq/cds-web/overlays/modal/ModalHeader';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

export type SettingsFieldType = 'org_name' | 'legal_entity_name';

export type EditFieldModalProps = {
  visible: boolean;
  title: string;
  fieldType: SettingsFieldType;
  initialValue: string;
  onSave: (value: string) => void;
  onClose: () => void;
  placeholder?: string;
};

const messages = defineMessages({
  save: {
    id: 'Settings.editFieldModal.save',
    defaultMessage: 'Save',
    description: 'Button label to save an edited Settings field',
  },
  cancel: {
    id: 'Settings.editFieldModal.cancel',
    defaultMessage: 'Cancel',
    description: 'Button label to cancel editing a Settings field',
  },
  close: {
    id: 'Settings.editFieldModal.close',
    defaultMessage: 'Close',
    description: 'Accessibility label to close the Settings edit field modal',
  },
});

export function EditFieldModal({
  visible,
  title,
  fieldType,
  initialValue,
  onSave,
  onClose,
  placeholder,
}: EditFieldModalProps) {
  const { formatMessage } = useIntl();
  const [value, setValue] = useState(initialValue);
  const previousVisible = useRef(false);

  useEffect(() => {
    if (visible && !previousVisible.current) {
      setValue(initialValue);
    }

    previousVisible.current = visible;
  }, [initialValue, visible]);

  const trimmedValue = value.trim();
  const trimmedInitialValue = initialValue.trim();
  const isSaveDisabled = trimmedValue === '' || trimmedValue === trimmedInitialValue;

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  }, []);

  const handleSave = useCallback(() => {
    if (isSaveDisabled) {
      return;
    }

    logEvent('settings_field_edited', {
      componentType: ComponentType.button,
      action: ActionType.click,
      field_type: fieldType,
    });
    onSave(trimmedValue);
  }, [fieldType, isSaveDisabled, onSave, trimmedValue]);

  return (
    <Modal visible={visible} onRequestClose={onClose}>
      <ModalHeader title={title} closeAccessibilityLabel={formatMessage(messages.close)} />
      <ModalBody>
        <TextInput
          accessibilityLabel={title}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          autoFocus
        />
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button onClick={handleSave} variant="primary" disabled={isSaveDisabled} compact>
            {formatMessage(messages.save)}
          </Button>
        }
        secondaryAction={
          <Button onClick={onClose} variant="secondary" compact>
            {formatMessage(messages.cancel)}
          </Button>
        }
      />
    </Modal>
  );
}
